/**
 * Shared column config for payment/check lists (operator checks and registrant payments).
 */

import type { ColumnProperties } from "@/components/non_registrant/dashboard-table";
import { Badge } from "@/components/ui/badge";
import type { ManualCheckPaymentListItem } from "@/hera-api/manual-check-payments-api";
import { pacificCalendarDateYmd } from "@/lib/overdue-invoices";
import {
  compareDateStrings,
  formatCurrency,
  formatDateNoTimezone,
  formatDateTime,
} from "@/lib/utils";

const MULTIPLE_VALUES_LABEL = "Multiple Licenses";

function CheckListCell({ value }: { value: string }) {
  if (value === MULTIPLE_VALUES_LABEL) {
    return (
      <span className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground italic">
        {value}
      </span>
    );
  }
  return <span>{value}</span>;
}

export const CHECK_ACCESSOR_KEYS = [
  "check_id",
  "property_address",
  "check_number",
  "payment_date",
  "status",
  "registrant_name",
  "registrant_company_name",
  "license_display_id",
  "jurisdiction_name",
  "license_type_name",
  "ams_company_name",
  "total_amount",
  "created_at",
] as const;

/** Registrant portal payments table: same as checks list but without Company (redundant for self). */
export const REGISTRANT_PAYMENTS_ACCESSOR_KEYS = [
  "check_id",
  "property_address",
  "check_number",
  "payment_date",
  "status",
  "registrant_name",
  "license_display_id",
  "jurisdiction_name",
  "license_type_name",
  "ams_company_name",
  "total_amount",
  "created_at",
] as const;

export type CheckAccessorKeys = (typeof CHECK_ACCESSOR_KEYS)[number];
export type RegistrantPaymentsAccessorKeys =
  (typeof REGISTRANT_PAYMENTS_ACCESSOR_KEYS)[number];
export type CheckColumnProperties = ColumnProperties<
  ManualCheckPaymentListItem,
  CheckAccessorKeys
>;

function getCheckStatusBadgeVariant(status: string) {
  switch (status) {
    case "Awaiting Check":
    case "Awaiting Payment":
    case "Payment Pending":
      return "secondary";
    case "Finalized":
    case "Succeeded":
      return "default";
    case "Cancelled":
    case "Failed":
    case "Refunded":
      return "destructive";
    default:
      return "outline";
  }
}

export function getCheckColumnProperties(
  accessorKey: CheckAccessorKeys,
): CheckColumnProperties {
  switch (accessorKey) {
    case "check_id":
      return {
        displayName: "Payment ID",
        defaultVisible: true,
        getValue: (row) =>
          row.check_id ??
          (row as unknown as Record<string, unknown>).checkId ??
          "",
        renderCell: (row) => {
          const value =
            row.check_id ??
            (row as unknown as Record<string, unknown>).checkId ??
            "";
          return <CheckListCell value={value || "—"} />;
        },
      };
    case "check_number":
      return {
        displayName: "Check #",
        defaultVisible: false,
        getValue: (row) => row.check_number ?? "",
        renderCell: (row) => (
          <span>{row.check_number?.trim() ? row.check_number : "—"}</span>
        ),
      };
    case "payment_date":
      return {
        displayName: "Payment Date",
        defaultVisible: true,
        getValue: (row) => row.payment_date ?? "",
        sortingFn: (rowA, rowB) =>
          compareDateStrings(
            rowA.original.payment_date,
            rowB.original.payment_date,
          ),
        renderCell: (row) => (
          <span>
            {row.payment_date ? formatDateNoTimezone(row.payment_date) : "—"}
          </span>
        ),
      };
    case "status":
      return {
        displayName: "Status",
        defaultVisible: true,
        allowBlankFilter: false,
        getValue: (row) => row.status,
        renderCell: (row) => (
          <Badge variant={getCheckStatusBadgeVariant(row.status)}>
            {row.status}
          </Badge>
        ),
      };
    case "registrant_name":
      return {
        displayName: "Registrant",
        defaultVisible: true,
        getValue: (row) => row.registrant_name ?? "",
        renderCell: (row) => (
          <CheckListCell value={row.registrant_name ?? ""} />
        ),
      };
    case "registrant_company_name":
      return {
        displayName: "Company",
        defaultVisible: true,
        getValue: (row) => row.registrant_company_name ?? "",
        renderCell: (row) => (
          <CheckListCell value={row.registrant_company_name ?? ""} />
        ),
      };
    case "license_display_id":
      return {
        displayName: "License ID",
        defaultVisible: true,
        allowBlankFilter: false,
        getValue: (row) => row.license_display_id,
        renderCell: (row) => <CheckListCell value={row.license_display_id} />,
      };
    case "jurisdiction_name":
      return {
        displayName: "Jurisdiction",
        defaultVisible: true,
        allowBlankFilter: false,
        getValue: (row) => row.jurisdiction_name,
        renderCell: (row) => <CheckListCell value={row.jurisdiction_name} />,
      };
    case "property_address":
      return {
        displayName: "Property Address",
        defaultVisible: true,
        getValue: (row) => row.property_address ?? "",
        renderCell: (row) => (
          <CheckListCell value={row.property_address ?? ""} />
        ),
      };
    case "license_type_name":
      return {
        displayName: "License Type",
        defaultVisible: true,
        allowBlankFilter: false,
        getValue: (row) => row.license_type_name,
        renderCell: (row) => <CheckListCell value={row.license_type_name} />,
      };
    case "ams_company_name":
      return {
        displayName: "AMS",
        defaultVisible: true,
        getValue: (row) => row.ams_company_name ?? "",
        renderCell: (row) => (
          <CheckListCell
            value={row.ams_company_name?.trim() ? row.ams_company_name : "—"}
          />
        ),
      };
    case "total_amount":
      return {
        displayName: "Total",
        defaultVisible: true,
        allowBlankFilter: false,
        getValue: (row) => {
          const reg = Number.parseFloat(row.total_registration_fee);
          const late = Number.parseFloat(row.total_late_fee);
          const proc = Number.parseFloat(row.total_processing_fee);
          const insp = Number.parseFloat(row.total_inspection_fee || "0");
          return formatCurrency(reg + late + proc + insp);
        },
      };
    case "created_at":
      return {
        displayName: "Created",
        defaultVisible: false,
        allowBlankFilter: false,
        getValue: (row) => formatDateTime(row.created_at),
        getFilterValue: (row) =>
          pacificCalendarDateYmd(row.created_at) ?? row.created_at,
        sortingFn: (rowA, rowB) =>
          compareDateStrings(
            rowA.original.created_at,
            rowB.original.created_at,
          ),
      };
    default:
      return {
        displayName: accessorKey,
        defaultVisible: false,
        getValue: (row) =>
          (row as unknown as Record<string, string>)[accessorKey] ?? "",
      };
  }
}
